import { useEffect, useRef } from 'react';
import { Line, type Canvas, type FabricObject } from 'fabric';
import generatedManifest from '../assets/badge-templates-manifest.json';

/* ------------------------------------------------------------------ */
/*  Constants                                                          */
/* ------------------------------------------------------------------ */

export const CANVAS_SIZE = 450;
export const MAX_UNDO = 20;
export const SNAP_PX = 5;

/* ------------------------------------------------------------------ */
/*  Uploaded-image downscaling                                         */
/* ------------------------------------------------------------------ */

/**
 * Fit (w,h) within a square of `maxEdge` on the longest side, never upscaling.
 * Pure (no DOM) so it can be unit-tested; `downscaleImageDataUrl` uses it.
 */
export function fitWithin(w: number, h: number, maxEdge: number): { w: number; h: number; scale: number } {
  if (!w || !h) return { w, h, scale: 1 };
  const scale = Math.min(1, maxEdge / Math.max(w, h));
  return { w: Math.max(1, Math.round(w * scale)), h: Math.max(1, Math.round(h * scale)), scale };
}

/**
 * Shrink a raster-image data URL so its longest edge is <= maxEdge, re-encoding
 * in its ORIGINAL format (PNG→PNG keeps alpha + crisp logos; JPEG→JPEG). Uploaded
 * images are embedded as base64 inside the Fabric JSON/SVG, and a full-resolution
 * photo there pushes the designer's Preview/Create request past the 6 MB AWS
 * Lambda sync-invoke payload limit (API Gateway 413s before the handler runs),
 * which is why Preview silently "cannot render". Vector (SVG) and already-small
 * images pass through untouched. Best-effort: returns the original on any failure.
 */
export function downscaleImageDataUrl(
  dataUrl: string,
  { maxEdge = 1280, quality = 0.9 }: { maxEdge?: number; quality?: number } = {},
): Promise<string> {
  return new Promise((resolve) => {
    try {
      if (typeof dataUrl !== 'string' || !dataUrl.startsWith('data:image/')) { resolve(dataUrl); return; }
      // Vector art has no pixel dimensions to cap and stays compact — leave as-is.
      if (dataUrl.startsWith('data:image/svg')) { resolve(dataUrl); return; }
      const mime = dataUrl.slice(5, (dataUrl.indexOf(';') + 1 || 6) - 1) || 'image/png';
      const img = new Image();
      img.onload = () => {
        try {
          const { naturalWidth: w, naturalHeight: h } = img;
          const { w: cw, h: ch, scale } = fitWithin(w, h, maxEdge);
          // Already within bounds AND small on the wire — keep the original bytes.
          if (scale === 1 && dataUrl.length < 700 * 1024) { resolve(dataUrl); return; }
          const canvas = document.createElement('canvas');
          canvas.width = cw; canvas.height = ch;
          const ctx = canvas.getContext('2d');
          if (!ctx) { resolve(dataUrl); return; }
          ctx.drawImage(img, 0, 0, cw, ch);
          // Preserve the source format: JPEG re-encodes photos small; PNG stays
          // lossless (keeps transparency and sharp logo/text edges).
          const out = mime === 'image/jpeg'
            ? canvas.toDataURL('image/jpeg', quality)
            : canvas.toDataURL('image/png');
          resolve(out.length < dataUrl.length ? out : dataUrl);
        } catch { resolve(dataUrl); }
      };
      img.onerror = () => resolve(dataUrl);
      img.src = dataUrl;
    } catch { resolve(dataUrl); }
  });
}

export interface TemplateEntry {
  id: string;
  svg: string;
  thumb: string;
  shape?: string;
  palette?: string;
  shapeLabel?: string;
  paletteLabel?: string;
  label?: string;
  // Named styles (e.g. "recognition") — distinctive template families rendered
  // across every palette, each with a landing page. Optional on plain baselines.
  style?: string;
  styleLabel?: string;
  category?: string;
  name?: string;
  // Editable placeholder layers added on top of the frame when the template is
  // applied — each becomes a normal selectable/editable/deletable text object.
  layers?: TemplateLayer[];
}

// A composed template's editable placeholder layers. Each becomes a normal
// selectable/editable/deletable Fabric object on top of the frame. Text layers
// are the original kind; icon/shape layers (added 2026-07 for the Certifier-style
// families) load an SVG from our asset libraries as an independent, recolourable,
// movable object — so seals/emblems/ribbons are truly LAYERED, not baked-in.
export interface TemplateTextLayer {
  type: 'text';
  text: string;
  top: number;
  left?: number;
  fontSize: number;
  fontFamily: string;
  fill: string;
  fontWeight?: string;
  charSpacing?: number;
}
export interface TemplateIconLayer {
  type: 'icon';
  // Icon name from ICON_NAMES → /assets/badge-icons/icon-<name>.svg
  icon: string;
  top: number;
  left?: number;
  // Target display size (px) in the 450 designer-canvas space; the SVG is
  // uniformly scaled to fit this box.
  size: number;
  fill?: string;
}
export interface TemplateShapeLayer {
  type: 'shape';
  // 'shape' → /assets/badge-shapes/shape-NNN.svg, 'ribbon' → /assets/badge-ribbons/ribbon-NNN.svg
  kind: 'shape' | 'ribbon';
  num: number;
  top: number;
  left?: number;
  size: number;
  fill?: string;
}
export type TemplateLayer = TemplateTextLayer | TemplateIconLayer | TemplateShapeLayer;

const HANDCRAFTED_TEMPLATES: TemplateEntry[] = Array.from({ length: 20 }, (_, i) => ({
  id: `Template-${String(i).padStart(3, '0')}`,
  svg: `/assets/badge-templates/Template-${String(i).padStart(3, '0')}.svg`,
  thumb: `/assets/badge-templates/Template-${String(i).padStart(3, '0')}.png`,
  label: `Template ${String(i).padStart(3, '0')}`,
}));

export const GENERATED_TEMPLATES: TemplateEntry[] =
  (generatedManifest as { templates: TemplateEntry[] }).templates;

export const TEMPLATE_SHAPES = (generatedManifest as { shapes: Record<string, string> }).shapes;
export const TEMPLATE_PALETTES = (generatedManifest as { palettes: Record<string, string> }).palettes;
export const TEMPLATE_STYLES =
  (generatedManifest as { styles?: Record<string, string> }).styles ?? {};

export interface StyleMeta {
  label: string;
  category: string;
  slug?: string;
  description?: string;
  default: string;
  colors: string[];
}
export const TEMPLATE_STYLE_META =
  (generatedManifest as { styleMeta?: Record<string, StyleMeta> }).styleMeta ?? {};

// The Templates panel holds only fully-composed designs — a frame PLUS editable
// placeholder text layers (recognition, …). Bare frames with no layers (the
// handcrafted set and the plain shape×palette baselines) are "background shapes"
// and live under the Elements menu instead, so Templates stays curated.
const STYLED_TEMPLATES = GENERATED_TEMPLATES.filter((t) => t.style);
const BASELINE_TEMPLATES = GENERATED_TEMPLATES.filter((t) => !t.style);
const ALL_TEMPLATES: TemplateEntry[] = [
  ...STYLED_TEMPLATES,
  ...HANDCRAFTED_TEMPLATES,
  ...BASELINE_TEMPLATES,
];
export const TEMPLATES: TemplateEntry[] = ALL_TEMPLATES.filter((t) => t.layers?.length);
// Layer-less frames, applied as the badge background from Elements → Background
// shapes. Composed designs lead, then handcrafted, then shape×palette baselines.
export const BACKGROUND_TEMPLATES: TemplateEntry[] = ALL_TEMPLATES.filter((t) => !t.layers?.length);

export const SHAPE_CATEGORIES = [
  { name: 'Circles', range: [1, 10] as const },
  { name: 'Shields', range: [11, 20] as const },
  { name: 'Hexagons', range: [21, 28] as const },
  { name: 'Stars', range: [29, 36] as const },
  { name: 'Medals', range: [37, 44] as const },
  { name: 'Seals', range: [45, 54] as const },
  { name: 'Wreaths', range: [55, 60] as const },
  { name: 'Banners', range: [61, 70] as const },
  { name: 'Geometric', range: [71, 80] as const },
];

// Ribbon library (scripts/generate-ribbons.mjs) grouped into categories —
// ranges must match the generator's output order.
export const RIBBON_CATEGORIES = [
  { name: 'Straight', range: [1, 7] as const },
  { name: 'Pointed', range: [8, 14] as const },
  { name: 'Tails', range: [15, 21] as const },
  { name: 'Decorative', range: [22, 28] as const },
];
export const RIBBON_COUNT = 28;

export const ICON_NAMES = [
  'atom','award','badge-id','book','brain','briefcase','building',
  'calculator','camera','certificate','chart-bar','checkmark','clipboard',
  'code','crown','diamond','diploma','flame','gear','globe',
  'graduation-cap','handshake','heart','infinity','key','leaf',
  'lightbulb','lightning','lock','medal','megaphone','microscope',
  'music-note','palette','pencil','plus','podium','puzzle',
  'ribbon-badge','rocket','ruler','shield-check','star','sun',
  'target','thumbs-up','torch','trophy','users','wrench',
  // Added 2026-07 — general-purpose set.
  'calendar','clock','mail','bell','flag','bookmark','tag','gift',
  'check-circle','link','share','download','upload','chart-line',
  'map-pin','sparkle','coffee','hand','bar-chart','medal-star',
];

// Curated font library, grouped by category (matching the reference picker's
// Handwriting / Serif / Sans / Mono organization). Every Google family here is
// validated against the fonts.googleapis.com/css2 API — an invalid name 400s
// the *entire* batch request and silently breaks all fonts, so only add names
// confirmed to resolve. System fonts (first group) are never fetched.
export const SYSTEM_FONTS = ['Arial', 'Georgia', 'Times New Roman'];

export const FONT_GROUPS: { label: string; fonts: string[] }[] = [
  { label: 'System', fonts: SYSTEM_FONTS },
  { label: 'Sans', fonts: [
    'Montserrat', 'Lato', 'Raleway', 'Oswald', 'Open Sans', 'Roboto', 'Poppins',
    'Outfit', 'DM Sans', 'Noto Sans', 'Quicksand', 'Ubuntu', 'Prompt', 'Cairo',
    'Teko', 'Tenor Sans', 'Barlow Condensed', 'Bricolage Grotesque', 'Golos Text',
    'Google Sans Flex',
  ] },
  { label: 'Serif', fonts: [
    'Playfair Display', 'Merriweather', 'Cormorant', 'EB Garamond',
    'Libre Baskerville', 'Baskervville', 'Cinzel', 'Marcellus', 'PT Serif',
    'Roboto Slab', 'Noto Serif Display', 'Newsreader', 'Bellefair', 'Inika',
    'Josefin Slab', 'Kameron',
  ] },
  { label: 'Handwriting', fonts: [
    'Pacifico', 'Dancing Script', 'Great Vibes', 'Lobster', 'Satisfy',
    'Alex Brush', 'Kalam', 'Marck Script', 'Whisper', 'Birthstone', 'Bokor',
    'Ephesis', 'MonteCarlo',
  ] },
  { label: 'Mono', fonts: ['Space Mono'] },
];

// Flat list (backwards-compatible with existing consumers).
export const FONTS = FONT_GROUPS.flatMap((g) => g.fonts);

const GOOGLE_FONTS = FONTS.filter((f) => !SYSTEM_FONTS.includes(f));

export const TEXT_VARIABLES = [
  { key: '{{recipient_name}}', label: 'Recipient Name', placeholder: 'Recipient Name' },
  { key: '{{date}}', label: 'Issue Date', placeholder: 'Issue Date' },
  { key: '{{issuer_name}}', label: 'Issuer Name', placeholder: 'Issuer Name' },
] as const;

export type TextVariable = (typeof TEXT_VARIABLES)[number];

/* ------------------------------------------------------------------ */
/*  Helpers                                                            */
/* ------------------------------------------------------------------ */

export function loadGoogleFonts() {
  if (document.getElementById('badge-designer-fonts')) return;
  const link = document.createElement('link');
  link.id = 'badge-designer-fonts';
  link.rel = 'stylesheet';
  link.href = `https://fonts.googleapis.com/css2?${GOOGLE_FONTS.map((f) => `family=${f.replace(/ /g, '+')}`).join('&')}&display=swap`;
  document.head.appendChild(link);
}

export function clampObject(obj: FabricObject) {
  const bound = obj.getBoundingRect();
  if (bound.left < 0) obj.left! -= bound.left;
  if (bound.top < 0) obj.top! -= bound.top;
  if (bound.left + bound.width > CANVAS_SIZE) obj.left! -= bound.left + bound.width - CANVAS_SIZE;
  if (bound.top + bound.height > CANVAS_SIZE) obj.top! -= bound.top + bound.height - CANVAS_SIZE;
}

export function padNum(n: number, len = 3) {
  return String(n).padStart(len, '0');
}

/** Recursively set fill on all objects within a group / SVG. */
export function recolorSvgObject(obj: FabricObject, color: string) {
  if ((obj as any)._objects) {
    for (const child of (obj as any)._objects) recolorSvgObject(child, color);
  } else {
    if (obj.fill && obj.fill !== 'none' && obj.fill !== 'transparent') obj.set('fill', color);
    if (obj.stroke && obj.stroke !== 'none' && obj.stroke !== 'transparent') obj.set('stroke', color);
  }
}

/* ------------------------------------------------------------------ */
/*  Alignment guides (center snap)                                     */
/* ------------------------------------------------------------------ */

export function setupAlignmentGuides(canvas: Canvas) {
  let hLine: FabricObject | null = null;
  let vLine: FabricObject | null = null;

  const makeLine = (coords: [number, number, number, number]) => {
    return new Line(coords, {
      stroke: '#9333ea', strokeWidth: 1, strokeDashArray: [4, 4],
      selectable: false, evented: false, excludeFromExport: true,
    });
  };

  canvas.on('object:moving', (e) => {
    const obj = e.target;
    const cx = obj.left! + (obj.width! * (obj.scaleX ?? 1)) / 2;
    const cy = obj.top! + (obj.height! * (obj.scaleY ?? 1)) / 2;
    const mid = CANVAS_SIZE / 2;

    if (Math.abs(cx - mid) < SNAP_PX) {
      obj.left = mid - (obj.width! * (obj.scaleX ?? 1)) / 2;
      if (!vLine) { vLine = makeLine([mid, 0, mid, CANVAS_SIZE]); canvas.add(vLine); }
    } else if (vLine) { canvas.remove(vLine); vLine = null; }

    if (Math.abs(cy - mid) < SNAP_PX) {
      obj.top = mid - (obj.height! * (obj.scaleY ?? 1)) / 2;
      if (!hLine) { hLine = makeLine([0, mid, CANVAS_SIZE, mid]); canvas.add(hLine); }
    } else if (hLine) { canvas.remove(hLine); hLine = null; }
  });

  const clearGuides = () => {
    if (hLine) { canvas.remove(hLine); hLine = null; }
    if (vLine) { canvas.remove(vLine); vLine = null; }
  };
  canvas.on('object:modified', clearGuides);
  canvas.on('mouse:up', clearGuides);
  return clearGuides;
}

/* ------------------------------------------------------------------ */
/*  Popover grid component                                             */
/* ------------------------------------------------------------------ */

interface PopoverGridProps {
  open: boolean;
  onClose: () => void;
  anchorRef: React.RefObject<HTMLDivElement | null>;
  children: React.ReactNode;
  title?: string;
}

export function PopoverGrid({ open, onClose, anchorRef, children, title }: PopoverGridProps) {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node) &&
          anchorRef.current && !anchorRef.current.contains(e.target as Node)) {
        onClose();
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open, onClose, anchorRef]);

  if (!open) return null;
  return (
    <div ref={ref} className="absolute left-0 top-full z-30 mt-1 w-72 rounded-lg border border-gray-200 bg-white shadow-lg">
      {title && <div className="border-b border-gray-100 px-3 py-2 text-xs font-semibold text-gray-500">{title}</div>}
      <div className="max-h-64 overflow-y-auto p-2">{children}</div>
    </div>
  );
}
